import { useTranslation } from 'react-i18next';
import TopBar from '../home/components/TopBar';
import MainNav from '../home/components/MainNav';
import Footer from '../home/components/Footer';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createServiceSchema } from '../../utils/seo';

export default function IntegratedSolutionPage() {
  const { t } = useTranslation();

  useSEO({
    title: '통합 솔루션 - KR STUDIO | 올인원 이러닝 플랫폼',
    description: 'KR스튜디오의 통합 솔루션으로 콘텐츠 제작부터 LMS 운영까지 한 번에 해결하세요. 완벽한 이러닝 생태계를 구축합니다.',
    keywords: '통합 솔루션, 이러닝 플랫폼, LMS, 올인원 솔루션, 교육 시스템',
    canonical: '/integrated-solution',
    schema: createServiceSchema(
      '통합 솔루션',
      '콘텐츠 제작부터 LMS 운영까지 완벽한 이러닝 생태계 구축'
    ),
  });

  const features = [
    {
      icon: 'ri-video-line',
      title: '콘텐츠 제작',
      description: '기획부터 제작까지 원스톱 서비스'
    },
    {
      icon: 'ri-live-line',
      title: '라이브 스트리밍',
      description: '안정적인 실시간 방송 시스템'
    },
    {
      icon: 'ri-database-2-line',
      title: 'LMS 연동',
      description: '학습관리시스템 완벽 통합'
    },
    {
      icon: 'ri-cloud-line',
      title: '클라우드 저장',
      description: '안전한 콘텐츠 보관 및 관리'
    },
    {
      icon: 'ri-bar-chart-line',
      title: '분석 리포트',
      description: '시청 데이터 및 학습 분석'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 지원',
      description: '전담 매니저 운영 지원'
    }
  ];

  const benefits = [
    {
      title: '비용 절감',
      description: '개별 발주 대비 30% 이상 비용 절감',
      icon: 'ri-money-dollar-circle-line',
      stats: '30%↓'
    },
    {
      title: '시간 단축',
      description: '통합 관리로 제작 기간 50% 단축',
      icon: 'ri-time-line',
      stats: '50%↓'
    },
    {
      title: '품질 향상',
      description: '일관된 품질 관리 시스템',
      icon: 'ri-star-line',
      stats: '98%'
    },
    {
      title: '효율 증대',
      description: '원스톱 서비스로 업무 효율 극대화',
      icon: 'ri-rocket-line',
      stats: '2배↑'
    }
  ];

  const workflow = [
    { step: '기획', description: '콘텐츠 전략 수립', icon: 'ri-lightbulb-line' },
    { step: '제작', description: '스튜디오 촬영 및 편집', icon: 'ri-film-line' },
    { step: '송출', description: '라이브 또는 VOD 배포', icon: 'ri-broadcast-line' },
    { step: '관리', description: 'LMS 업로드 및 관리', icon: 'ri-settings-3-line' },
    { step: '분석', description: '학습 데이터 분석', icon: 'ri-line-chart-line' },
    { step: '개선', description: '피드백 반영 및 개선', icon: 'ri-refresh-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <MainNav />

      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/475436b92bc2017cf8ea5e8666fcdd9f.jpg"
            alt="통합 운영 솔루션"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/95 via-teal-800/90 to-emerald-900/80"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-6xl font-black text-white mb-6 leading-tight">
              통합 운영 솔루션
            </h1>
            <p className="text-2xl text-white/90 mb-8 leading-relaxed">
              콘텐츠 제작부터 운영까지<br />
              모든 것을 하나로
            </p>
            <div className="flex gap-4">
              <a href="mailto:contact@krstudio.com" className="bg-emerald-500 text-white px-10 py-4 text-lg font-bold hover:bg-emerald-600 transition cursor-pointer whitespace-nowrap hidden">
                솔루션 문의
              </a>
              <a href="#features" className="border-2 border-white text-white px-10 py-4 text-lg font-bold hover:bg-white hover:text-emerald-900 transition cursor-pointer whitespace-nowrap hidden">
                자세히 보기
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">핵심 기능</h2>
            <p className="text-xl text-gray-600">올인원 통합 솔루션</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 hover:shadow-xl transition-all group cursor-pointer">
                <div className="w-16 h-16 bg-emerald-500/10 flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-all">
                  <i className={`${feature.icon} text-3xl text-emerald-500 group-hover:text-white transition-all`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">솔루션 도입 효과</h2>
            <p className="text-xl text-gray-600">검증된 성과</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 text-center hover:shadow-xl transition-all cursor-pointer">
                <div className="w-20 h-20 bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-4xl text-emerald-500`}></i>
                </div>
                <div className="text-4xl font-black text-emerald-500 mb-3">{benefit.stats}</div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">운영 프로세스</h2>
            <p className="text-xl text-gray-600">체계적인 6단계 워크플로우</p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {workflow.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-gray-50 p-8 hover:shadow-xl transition-all cursor-pointer">
                    <div className="w-16 h-16 bg-emerald-500 flex items-center justify-center mx-auto mb-4">
                      <i className={`${item.icon} text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-center mb-2">{item.step}</h3>
                    <p className="text-gray-600 text-center">{item.description}</p>
                  </div>
                  {index < workflow.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <i className="ri-arrow-right-line text-3xl text-emerald-500"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">개별 발주 vs 통합 솔루션</h2>
          </div>
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8">
              <h3 className="text-2xl font-bold mb-6 text-gray-700">개별 발주 방식</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1"></i>
                  <span className="text-gray-600">여러 업체와 개별 계약 및 관리</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1"></i>
                  <span className="text-gray-600">업체 간 커뮤니케이션 어려움</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1"></i>
                  <span className="text-gray-600">일관성 없는 품질 관리</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1"></i>
                  <span className="text-gray-600">높은 관리 비용과 시간 소요</span>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-8 border-2 border-emerald-500">
              <h3 className="text-2xl font-bold mb-6 text-emerald-900">통합 솔루션</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mr-3 mt-1"></i>
                  <span className="text-gray-700 font-bold">단일 창구 원스톱 서비스</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mr-3 mt-1"></i>
                  <span className="text-gray-700 font-bold">통합 관리로 효율적 소통</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mr-3 mt-1"></i>
                  <span className="text-gray-700 font-bold">일관된 품질 보장</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mr-3 mt-1"></i>
                  <span className="text-gray-700 font-bold">비용 및 시간 대폭 절감</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-black mb-6">통합 솔루션으로 전환하세요</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            복잡한 콘텐츠 운영을 하나의 솔루션으로 간편하게
          </p>
          <a href="mailto:contact@krstudio.com" className="inline-block bg-emerald-500 text-white px-12 py-4 text-lg font-bold hover:bg-emerald-600 transition cursor-pointer whitespace-nowrap hidden">
            무료 컨설팅 신청
          </a>
        </div>
      </section>

      <Footer />
      <QuickMenu />
    </div>
  );
}
